import React, { useContext, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  ButtonGroup,
  Col,
  Form,
  OverlayTrigger,
  Row,
  Tooltip,
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import SoftBadge from '../../common/SoftBadge';
import Avatar from '../../common/Avatar';
import Flex from '../../common/Flex';
import EmailAttachment from './EmailAttachment';
import logo from '../../../../assets/img/icons/ciat.png';
import api from '../../../../api/req';
import meta from '../../../../meta';
import { CiatAppContext } from '../../../../providers';

function ActionButton({
  tooltip, icon, handleClick, variant = 'light',
}) {
  return (
    <OverlayTrigger
      overlay={<Tooltip style={{ position: 'fixed' }}>{tooltip}</Tooltip>}
    >
      <Button variant={variant} onClick={handleClick} className="shadow-none">
        <FontAwesomeIcon icon={icon} />
      </Button>
    </OverlayTrigger>
  );
}

function InboxRow({
  email, isSelectedItem, toggleSelectedItem, openSettings,
}) {
  const {
    id,
    read,
    star,
    time,
    title,
    author,
    description,
    message_type: type,
    attachments,
  } = email;

  const { auth } = useContext(CiatAppContext);
  const [marked, setMarked] = useState(star);
  const navlink = useMemo(
    () => (openSettings ? `/email/compose/${id}` : `/email/email-detail/${id}`),
    [openSettings, id],
  );

  const onChangeRead = async () => {
    const r = await api.post(`${meta.app.email.backendURL}setread/`, auth, { message_id: id });
    if (!r.ok && r.status !== 400) {
      throw new Error(`${r.status} ${r.statusText}`);
    }

    toast.success(`Повідоблення відмічено як ${read ? 'непрочитане' : 'прочитане'}`, { theme: 'colored' });
  };

  return (
    <Row
      className={classNames(
        'border-bottom border-200 hover-actions-trigger hover-shadow py-2 px-1 mx-0 align-items-center',
        {
          'bg-light': read,
        },
      )}
    >
      <ButtonGroup
        size="sm"
        className="hover-actions end-0 me-3 email-row-actions"
        style={{ width: '10rem' }}
      >
        <ActionButton
          tooltip={read ? 'Відмітити як непрочитаний' : 'Відмітити як прочитаний'}
          icon={read ? 'envelope' : 'envelope-open'}
          handleClick={onChangeRead}
        />
      </ButtonGroup>
      <Col xs="auto" className="d-none d-sm-block align-self-start">
        <Flex alignItems="center">
          <Form.Check
            type="checkbox"
            id="inboxBulkSelect"
            className="form-check mb-0 fs-0"
            checked={isSelectedItem(id)}
            onChange={() => toggleSelectedItem(id)}
          />
          <FontAwesomeIcon
            onClick={() => setMarked(!marked)}
            icon={marked ? 'star' : ['far', 'star']}
            // transform="down-2"
            className={classNames(
              'ms-1',
              { 'text-warning': marked, 'text-300': !marked },
              'cursor-pointer',
            )}
          />
        </Flex>
      </Col>
      <Col xs md={9} xxl={10}>
        <Row>
          <Col md={4} xl={3} xxl={2} className="ps-md-0 mb-1 mb-md-0">
            <Flex className="position-relative">
              <Avatar src={logo} size="s" rounded="soft" />
              <div className="flex-1 ms-2">
                <Link
                  to={navlink}
                  className={classNames('stretched-link inbox-link', {
                    'fw-bold': !read,
                  })}
                >
                  {author.repr}
                </Link>
                {type === 1 && (
                  <SoftBadge bg="info" className="ms-2">
                    інфо
                  </SoftBadge>
                )}
                {type === 2 && (
                  <SoftBadge bg="danger" className="ms-2">
                    важливо
                  </SoftBadge>
                )}
              </div>
            </Flex>
          </Col>
          <Col>
            <Link className="d-block inbox-link" to={navlink}>
              <span className={classNames({ 'fw-bold': !read })}>{title}</span>
              <span className="mx-1">&ndash;</span>
              <span>{description}</span>
            </Link>
            {attachments?.map((attachment) => (
              <EmailAttachment attachment={attachment} key={attachment.id} />
            ))}
          </Col>
        </Row>
      </Col>
      <Col
        xs="auto"
        as={Flex}
        direction="column"
        justifyContent="between"
        className="ms-auto align-self-start"
      >
        <span className={classNames({ 'fw-bold': !read })}>{time}</span>
      </Col>
    </Row>
  );
}

ActionButton.propTypes = {
  tooltip: PropTypes.string.isRequired,
  icon: PropTypes.string.isRequired,
  handleClick: PropTypes.func.isRequired,
  variant: PropTypes.string.isRequired,
};

InboxRow.propTypes = {
  email: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    img: PropTypes.string.isRequired,
    read: PropTypes.bool,
    star: PropTypes.bool,
    time: PropTypes.string,
    title: PropTypes.string,
    author: PropTypes.shape(),
    description: PropTypes.string,
    message_type: PropTypes.number,
    attachments: PropTypes.arrayOf({}),
  }).isRequired,
  isSelectedItem: PropTypes.func.isRequired,
  toggleSelectedItem: PropTypes.func.isRequired,
  openSettings: PropTypes.bool.isRequired,
};

export default InboxRow;
